import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  TrafficChannel,
  DashboardCell,
  ContractorDashboardRow,
  BranchCardData,
  BranchDashboardGroup,
} from '@ads-manager/shared';
import {
  dashboardApi,
  type ContractorChannelItem,
  type DashboardSummary,
} from '../api';
import { QUERY_KEYS, getStaleTime, STALE_TIMES, invalidateRelated } from './query-keys';

// Re-export for backward compatibility
export const TM_CONTRACTOR_CHANNELS_KEY = QUERY_KEYS.TM_CONTRACTOR_CHANNELS;

// Traffic Manager Dashboard hooks
export function useTMDashboard(year: number, month: number) {
  return useQuery<DashboardCell[]>({
    queryKey: [QUERY_KEYS.TM_DASHBOARD, year, month],
    queryFn: () => dashboardApi.getTrafficManagerDashboard(year, month),
    enabled: !!year && !!month,
    staleTime: getStaleTime(QUERY_KEYS.TM_DASHBOARD),
  });
}

export function useTMDashboardByBranch(year: number, month: number) {
  return useQuery<BranchDashboardGroup[]>({
    queryKey: [QUERY_KEYS.TM_DASHBOARD_BY_BRANCH, year, month],
    queryFn: () => dashboardApi.getTrafficManagerByBranch(year, month),
    enabled: !!year && !!month,
    staleTime: getStaleTime(QUERY_KEYS.TM_DASHBOARD_BY_BRANCH),
  });
}

export function useTMSummary(year: number, month: number) {
  return useQuery<DashboardSummary>({
    queryKey: [QUERY_KEYS.TM_SUMMARY, year, month],
    queryFn: () => dashboardApi.getTrafficManagerSummary(year, month),
    enabled: !!year && !!month,
    staleTime: getStaleTime(QUERY_KEYS.TM_SUMMARY),
  });
}

// Traffic Manager view of contractor dashboard hooks
export function useTMContractorDashboard(
  contractorId: string | undefined,
  channel: TrafficChannel | null,
  year: number,
  month: number,
) {
  return useQuery<ContractorDashboardRow[]>({
    queryKey: [QUERY_KEYS.TM_CONTRACTOR_VIEW, contractorId, channel?.id, year, month],
    queryFn: () => dashboardApi.getContractorDashboardForTM(contractorId!, channel!.id, year, month),
    enabled: !!contractorId && !!channel?.id && !!year && !!month,
    staleTime: STALE_TIMES.DYNAMIC,
  });
}

export function useTMContractorSummary(
  contractorId: string | undefined,
  channel: TrafficChannel | null,
  year: number,
  month: number,
) {
  return useQuery<DashboardSummary>({
    queryKey: [QUERY_KEYS.TM_CONTRACTOR_SUMMARY, contractorId, channel?.id, year, month],
    queryFn: () => dashboardApi.getContractorSummaryForTM(contractorId!, channel!.id, year, month),
    enabled: !!contractorId && !!channel?.id && !!year && !!month,
    staleTime: STALE_TIMES.DYNAMIC,
  });
}

export function useTMContractorChannels(contractorId: string | undefined) {
  return useQuery<ContractorChannelItem[]>({
    queryKey: [QUERY_KEYS.TM_CONTRACTOR_CHANNELS, contractorId],
    queryFn: () => dashboardApi.getContractorChannelsForTM(contractorId!),
    enabled: !!contractorId,
    // Channels are semi-static - use longer stale time to reduce requests
    staleTime: STALE_TIMES.SEMI_STATIC,
    // Removed refetchOnMount: 'always' - rely on invalidation instead
  });
}

// Contractor Dashboard hooks
export function useContractorDashboard(
  channel: TrafficChannel | null,
  year: number,
  month: number,
) {
  return useQuery<ContractorDashboardRow[]>({
    queryKey: [QUERY_KEYS.CONTRACTOR_DASHBOARD, channel?.id, year, month],
    queryFn: () => dashboardApi.getContractorDashboard(channel!.id, year, month),
    enabled: !!channel?.id && !!year && !!month,
    staleTime: getStaleTime(QUERY_KEYS.CONTRACTOR_DASHBOARD),
  });
}

export function useContractorSummary(
  channel: TrafficChannel | null,
  year: number,
  month: number,
) {
  return useQuery<DashboardSummary>({
    queryKey: [QUERY_KEYS.CONTRACTOR_SUMMARY, channel?.id, year, month],
    queryFn: () => dashboardApi.getContractorSummary(channel!.id, year, month),
    enabled: !!channel?.id && !!year && !!month,
    staleTime: getStaleTime(QUERY_KEYS.CONTRACTOR_SUMMARY),
  });
}

export function useContractorChannels() {
  return useQuery<ContractorChannelItem[]>({
    queryKey: [QUERY_KEYS.CONTRACTOR_CHANNELS],
    queryFn: () => dashboardApi.getContractorChannels(),
    // Channels are semi-static - use longer stale time to reduce requests
    staleTime: STALE_TIMES.SEMI_STATIC,
    // Removed refetchOnMount: 'always' and refetchOnWindowFocus
    // Rely on mutation invalidation instead of aggressive refetching
  });
}

// Branch Card hook
export function useBranchCard(
  branchId: string | undefined,
  trafficChannelId: string | null,
  year: number,
  month: number,
) {
  return useQuery<BranchCardData>({
    queryKey: [QUERY_KEYS.BRANCH_CARD, branchId, trafficChannelId, year, month],
    queryFn: () =>
      dashboardApi.getBranchCardData(branchId!, trafficChannelId!, year, month),
    enabled: !!branchId && !!trafficChannelId && !!year && !!month,
    staleTime: getStaleTime(QUERY_KEYS.BRANCH_CARD),
  });
}

// Traffic Manager Branch Card hook (read-only view)
export function useTMBranchCard(
  contractorId: string | undefined,
  branchId: string | undefined,
  trafficChannelId: string | null,
  year: number,
  month: number,
) {
  return useQuery<BranchCardData>({
    queryKey: [QUERY_KEYS.TM_BRANCH_CARD, contractorId, branchId, trafficChannelId, year, month],
    queryFn: () =>
      dashboardApi.getBranchCardDataForTM(contractorId!, branchId!, trafficChannelId!, year, month),
    enabled: !!contractorId && !!branchId && !!trafficChannelId && !!year && !!month,
    staleTime: STALE_TIMES.DYNAMIC,
  });
}

// Toggle "в работе" статус плана для подрядчика
export function useToggleContractorWorking() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      branchId: string;
      trafficChannelId: string;
      year: number;
      month: number;
      isWorking: boolean;
    }) =>
      dashboardApi.toggleWorkingStatus(
        params.branchId,
        params.trafficChannelId,
        params.year,
        params.month,
        params.isWorking,
      ),
    onSuccess: () => {
      invalidateRelated(
        queryClient,
        QUERY_KEYS.BRANCH_CARD,
        QUERY_KEYS.CONTRACTOR_DASHBOARD,
        QUERY_KEYS.TM_DASHBOARD,
        QUERY_KEYS.TM_DASHBOARD_BY_BRANCH,
        QUERY_KEYS.TM_CONTRACTOR_VIEW,
        QUERY_KEYS.TM_BRANCH_CARD,
        // Включение «в работе» списывает фикс. ставку с баланса
        QUERY_KEYS.BALANCE,
        // ...и может создать/пробудить уведомление о низком балансе —
        // бейдж инбокса обновляем сразу, не дожидаясь поллинга
        QUERY_KEYS.NOTIFICATIONS,
        QUERY_KEYS.NOTIFICATIONS_UNREAD,
      );
    },
  });
}

// Toggle "в работе" статус плана для TM
export function useToggleTMWorking() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      contractorId: string;
      branchId: string;
      trafficChannelId: string;
      year: number;
      month: number;
      isWorking: boolean;
    }) =>
      dashboardApi.toggleWorkingStatusForTM(
        params.contractorId,
        params.branchId,
        params.trafficChannelId,
        params.year,
        params.month,
        params.isWorking,
      ),
    onSuccess: () => {
      invalidateRelated(
        queryClient,
        QUERY_KEYS.BRANCH_CARD,
        QUERY_KEYS.CONTRACTOR_DASHBOARD,
        QUERY_KEYS.TM_DASHBOARD,
        QUERY_KEYS.TM_DASHBOARD_BY_BRANCH,
        QUERY_KEYS.TM_CONTRACTOR_VIEW,
        QUERY_KEYS.TM_BRANCH_CARD,
        // Включение «в работе» списывает фикс. ставку с баланса
        QUERY_KEYS.BALANCE,
        // ...и может создать/пробудить уведомление о низком балансе —
        // бейдж инбокса обновляем сразу, не дожидаясь поллинга
        QUERY_KEYS.NOTIFICATIONS,
        QUERY_KEYS.NOTIFICATIONS_UNREAD,
      );
    },
  });
}

// Available months for branch card
export function useAvailableMonths(
  branchId: string | undefined,
  trafficChannelId: string | null,
) {
  return useQuery<Array<{ year: number; month: number }>>({
    queryKey: [QUERY_KEYS.BRANCH_CARD, 'available-months', branchId, trafficChannelId],
    queryFn: () => dashboardApi.getAvailableMonths(branchId!, trafficChannelId!),
    enabled: !!branchId && !!trafficChannelId,
    staleTime: STALE_TIMES.SEMI_STATIC, // Available months don't change frequently
  });
}
